import React, { useState, useMemo } from 'react';
import { Popover, Button, ChoiceList, TextField } from '@shopify/polaris';

/**
 * ChoiceListFilter — column-header filter popover.
 * Props:
 *   activator       : React element (the funnel button) or a render function (active) => el
 *   choices         : [{ label, value }] — full list
 *   selected        : string[]
 *   onChange        : (string[]) => void
 *   onClear         : () => void
 *   searchable      : bool — show a search input (default false)
 *   searchLabel     : string — placeholder for search input
 *   active          : bool — whether the popover is open
 *   onClose         : () => void
 */
export default function ChoiceListFilter({
  activator,
  choices = [],
  selected = [],
  onChange,
  onClear,
  searchable = false,
  searchLabel = 'Search…',
  active,
  onClose,
}) {
  const [search, setSearch] = useState('');

  const filteredChoices = useMemo(() => {
    if (!searchable || !search.trim()) return choices;
    const q = search.trim().toLowerCase();
    // Labels may be JSX elements (e.g. the Delivery column renders an emoji +
    // text span), so `.toLowerCase()` on `c.label` would throw and blank the
    // page. Match on `searchValue` (which JSX-label choices carry), falling
    // back to `value`, and only use `label` directly when it's a plain string.
    return choices.filter(c => {
      const text = typeof c.label === 'string' ? c.label : (c.searchValue || c.value || '');
      return String(text).toLowerCase().includes(q);
    });
  }, [choices, search, searchable]);

  const handleClose = () => {
    setSearch('');
    onClose && onClose();
  };

  const handleApply = () => {
    handleClose();
  };

  const handleClear = () => {
    onClear && onClear();
    setSearch('');
    onClose && onClose();
  };

  return (
    <Popover
      active={active}
      activator={typeof activator === 'function' ? activator(active) : activator}
      onClose={handleClose}
      preferredAlignment="left"
      preferredPosition="below"
    >
      <div className="ots-filter-popover">
        {searchable && (
          <div className="ots-filter-search">
            <TextField
              label={searchLabel}
              labelHidden
              placeholder={searchLabel}
              value={search}
              onChange={setSearch}
              clearButton
              onClearButtonClick={() => setSearch('')}
              autoComplete="off"
              size="slim"
            />
          </div>
        )}

        <div className="ots-filter-choices">
          {filteredChoices.length === 0 ? (
            <div style={{ padding: '8px 4px', color: '#6d7175', fontSize: 13 }}>
              No options match.
            </div>
          ) : (
            <ChoiceList
              titleHidden
              title="Filter"
              allowMultiple
              choices={filteredChoices}
              selected={selected}
              onChange={onChange}
            />
          )}
        </div>

        <div className="ots-filter-actions">
          <Button size="slim" variant="primary" onClick={handleApply}>
            Apply
          </Button>
          <Button size="slim" onClick={handleClear}>
            Clear
          </Button>
        </div>
      </div>
    </Popover>
  );
}
